import React, { useState } from 'react';
import { X, RotateCcw, Save, FolderOpen, Download, Filter, ChevronDown, ChevronUp, List, LayoutGrid } from 'lucide-react';

const AIP_FILTER_SLOTS = 'aip_filter_slots';

const initialFilters = {
    search: '',
    status: 'all',
    minPrice: '',
    maxPrice: '',
    minCommissionRate: '',
    maxCommissionRate: '',
    minCommissionPerSale: '',
    minEstSales: '',
    maxEstSales: '',
    minEstRevenue: '',
    maxEstRevenue: '',
    minBSR: '',
    maxBSR: '',
    minRankDrops30: '',
    minRankDrops90: '',
    category: '',
    brand: '',
    amazonSells: 'all',
    minRating: '',
    minReviews: '',
    minHearts: '',
    maxHearts: '',
    minTotalInfVideos: '',
    maxTotalInfVideos: '',
    minTopInfVideos: '',
    maxTopInfVideos: '',
    minTopBrandVideos: '',
    maxTopBrandVideos: '',
    minBottomInfVideos: '',
    maxBottomInfVideos: '',
    minBottomBrandVideos: '',
    maxBottomBrandVideos: '',
    hasMainVideos: 'all',
    cc: 'all',
    sortBy: 'default',
};

const AIPAdvancedFilters = ({ filters, setFilters, categories = [], brands = [], creators = [], isCompactView, setIsCompactView, onExportCSV, totalCount, filteredCount }) => {
    const [expanded, setExpanded] = useState(false);
    const [showSaveSlots, setShowSaveSlots] = useState(false);
    const [showLoadSlots, setShowLoadSlots] = useState(false);

    const activeCount = Object.entries(filters).filter(([k, v]) => {
        if (k === 'search' || k === 'sortBy') return false;
        if ((k === 'status' || k === 'amazonSells' || k === 'hasMainVideos' || k === 'cc') && v === 'all') return false;
        if ((k === 'category' || k === 'brand') && !v) return false;
        return v !== '' && v !== initialFilters[k];
    }).length;

    const resetFilters = () => setFilters({ ...initialFilters, search: filters.search });

    const saveSlot = (slot) => {
        const slots = JSON.parse(localStorage.getItem(AIP_FILTER_SLOTS) || '{}');
        const name = prompt('Name this filter preset:', slots[slot]?.name || `Preset ${slot + 1}`);
        if (!name) return;
        slots[slot] = { name, filters };
        localStorage.setItem(AIP_FILTER_SLOTS, JSON.stringify(slots));
        setShowSaveSlots(false);
    };

    const loadSlot = (slot) => {
        const slots = JSON.parse(localStorage.getItem(AIP_FILTER_SLOTS) || '{}');
        if (slots[slot]) setFilters({ ...initialFilters, ...slots[slot].filters });
        setShowLoadSlots(false);
    };

    const slots = JSON.parse(localStorage.getItem(AIP_FILTER_SLOTS) || '{}');

    const RangeInput = ({ label, minKey, maxKey, prefix = '' }) => {
        const [localMin, setLocalMin] = React.useState(filters[minKey] ?? '');
        const [localMax, setLocalMax] = React.useState(filters[maxKey] ?? '');
        React.useEffect(() => { setLocalMin(filters[minKey] ?? ''); }, [filters[minKey]]);
        React.useEffect(() => { setLocalMax(filters[maxKey] ?? ''); }, [filters[maxKey]]);
        return (
            <div>
                <label className="text-[10px] uppercase font-semibold text-gray-400 block mb-1">{label}</label>
                <div className="flex gap-1">
                    <input type="number" placeholder={`${prefix}Min`} value={localMin}
                        onChange={e => setLocalMin(e.target.value)}
                        onBlur={e => setFilters(f => ({ ...f, [minKey]: e.target.value }))}
                        className="w-full px-2 py-1.5 border border-gray-200 rounded text-xs focus:ring-1 focus:ring-teal-500 focus:border-transparent" />
                    {maxKey && <input type="number" placeholder={`${prefix}Max`} value={localMax}
                        onChange={e => setLocalMax(e.target.value)}
                        onBlur={e => setFilters(f => ({ ...f, [maxKey]: e.target.value }))}
                        className="w-full px-2 py-1.5 border border-gray-200 rounded text-xs focus:ring-1 focus:ring-teal-500 focus:border-transparent" />}
                </div>
            </div>
        );
    };

    const MinInput = ({ label, filterKey, placeholder }) => {
        const [local, setLocal] = React.useState(filters[filterKey] ?? '');
        React.useEffect(() => { setLocal(filters[filterKey] ?? ''); }, [filters[filterKey]]);
        return (
            <div>
                <label className="text-[10px] uppercase font-semibold text-gray-400 block mb-1">{label}</label>
                <input type="number" placeholder={placeholder || 'Min'} value={local}
                    onChange={e => setLocal(e.target.value)}
                    onBlur={e => setFilters(f => ({ ...f, [filterKey]: e.target.value }))}
                    className="w-full px-2 py-1.5 border border-gray-200 rounded text-xs focus:ring-1 focus:ring-teal-500 focus:border-transparent" />
            </div>
        );
    };

    const SelectInput = ({ label, filterKey, options, defaultLabel = 'All' }) => (
        <div>
            <label className="text-[10px] uppercase font-semibold text-gray-400 block mb-1">{label}</label>
            <select value={filters[filterKey]}
                onChange={e => setFilters(f => ({ ...f, [filterKey]: e.target.value }))}
                className="w-full px-2 py-1.5 border border-gray-200 rounded text-xs bg-white">
                <option value="">{defaultLabel}</option>
                {options.map(o => typeof o === 'string'
                    ? <option key={o} value={o}>{o}</option>
                    : <option key={o.value} value={o.value}>{o.label}</option>
                )}
            </select>
        </div>
    );

    return (
        <div className="bg-white border border-gray-200 rounded-xl mb-4">
            {/* Top Bar */}
            <div className="px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3 flex-1 min-w-0 flex-wrap">
                    {/* Search */}
                    <div className="flex-1 min-w-[200px] max-w-md">
                        <input type="text" value={filters.search}
                            onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
                            placeholder="Search ASIN, title, creator, brand..."
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent" />
                    </div>

                    {/* Sort */}
                    <select value={filters.sortBy}
                        onChange={e => setFilters(f => ({ ...f, sortBy: e.target.value }))}
                        className="px-2 py-1.5 border border-gray-200 rounded-lg text-xs bg-white cursor-pointer">
                        <option value="default">Sort: Default</option>
                        <option value="uploaded_at">Upload Time (Newest)</option>
                        <option value="price_asc">Price: Low to High</option>
                        <option value="price_desc">Price: High to Low</option>
                        <option value="commission">Commission Rate</option>
                        <option value="commission_per_sale">Commission $/Sale</option>
                        <option value="est_sales">Est. Sales/mo</option>
                        <option value="est_revenue">Est. Revenue/mo</option>
                        <option value="bsr">BSR (Best First)</option>
                        <option value="rank_drops_30">Rank Drops 30d</option>
                        <option value="hearts">Hearts</option>
                        <option value="video_count">Video Count</option>
                        <option value="duration">Duration</option>
                        <option value="inf_videos">Inf. Videos on Page</option>
                    </select>
                </div>

                <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400">
                        {filteredCount !== totalCount ? `${filteredCount} / ${totalCount}` : totalCount} ASINs
                    </span>

                    {/* View toggle */}
                    <div className="flex bg-gray-100 rounded-lg p-0.5">
                        <button onClick={() => setIsCompactView(true)}
                            className={`p-1.5 rounded transition ${isCompactView ? 'bg-white shadow text-teal-700' : 'text-gray-400'}`}
                            title="Compact view">
                            <List size={14} />
                        </button>
                        <button onClick={() => setIsCompactView(false)}
                            className={`p-1.5 rounded transition ${!isCompactView ? 'bg-white shadow text-teal-700' : 'text-gray-400'}`}
                            title="Full card view">
                            <LayoutGrid size={14} />
                        </button>
                    </div>

                    {/* Advanced toggle */}
                    <button onClick={() => setExpanded(!expanded)}
                        className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold transition ${expanded ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                        <Filter size={12} /> Filters
                        {activeCount > 0 && (
                            <span className="bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-[9px] font-bold">{activeCount}</span>
                        )}
                        {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                    </button>
                </div>
            </div>

            {/* Expanded Panel */}
            {expanded && (
                <div className="border-t border-gray-100 px-4 py-4 space-y-4">
                    {/* Product & Commission */}
                    <div>
                        <div className="text-[9px] uppercase font-bold text-gray-400 tracking-wider mb-2">Product & Commission</div>
                        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                            <RangeInput label="Price ($)" minKey="minPrice" maxKey="maxPrice" prefix="$" />
                            <RangeInput label="Commission Rate (%)" minKey="minCommissionRate" maxKey="maxCommissionRate" />
                            <MinInput label="Min Comm. $/Sale" filterKey="minCommissionPerSale" placeholder="e.g. 0.50" />
                            <SelectInput label="Category" filterKey="category" options={categories} defaultLabel="All Categories" />
                            <SelectInput label="Brand" filterKey="brand" options={brands} defaultLabel="All Brands" />
                            <div>
                                <label className="text-[10px] uppercase font-semibold text-gray-400 block mb-1">Amazon Sells</label>
                                <div className="flex bg-gray-100 rounded-lg p-0.5">
                                    {[{ value: 'all', label: 'All' }, { value: 'no', label: 'No' }, { value: 'yes', label: 'Yes' }].map(o => (
                                        <button key={o.value}
                                            onClick={() => setFilters(f => ({ ...f, amazonSells: o.value }))}
                                            className={`flex-1 px-2 py-1.5 rounded text-[10px] font-bold uppercase tracking-wide transition ${filters.amazonSells === o.value ? 'bg-white shadow text-teal-700' : 'text-gray-400'}`}>
                                            {o.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Sales & Rank */}
                    <div>
                        <div className="text-[9px] uppercase font-bold text-gray-400 tracking-wider mb-2">Sales & Rank</div>
                        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                            <RangeInput label="Est. Sales/mo" minKey="minEstSales" maxKey="maxEstSales" />
                            <RangeInput label="Est. Revenue/mo ($)" minKey="minEstRevenue" maxKey="maxEstRevenue" prefix="$" />
                            <RangeInput label="BSR (Sales Rank)" minKey="minBSR" maxKey="maxBSR" />
                            <MinInput label="Min Rank Drops (30d)" filterKey="minRankDrops30" placeholder="e.g. 10" />
                            <MinInput label="Min Rank Drops (90d)" filterKey="minRankDrops90" placeholder="e.g. 30" />
                            <MinInput label="Min Rating" filterKey="minRating" placeholder="e.g. 4" />
                            <MinInput label="Min Reviews" filterKey="minReviews" placeholder="e.g. 100" />
                        </div>
                    </div>

                    {/* Influencer Videos */}
                    <div>
                        <div className="text-[9px] uppercase font-bold text-gray-400 tracking-wider mb-2">Influencer Videos</div>
                        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                            <RangeInput label="Hearts" minKey="minHearts" maxKey="maxHearts" />
                            <RangeInput label="Total Inf. Videos" minKey="minTotalInfVideos" maxKey="maxTotalInfVideos" />
                            <RangeInput label="Upper Inf. Videos" minKey="minTopInfVideos" maxKey="maxTopInfVideos" />
                            <RangeInput label="Upper Brand Videos" minKey="minTopBrandVideos" maxKey="maxTopBrandVideos" />
                            <RangeInput label="Bottom Inf. Videos" minKey="minBottomInfVideos" maxKey="maxBottomInfVideos" />
                            <RangeInput label="Bottom Brand Videos" minKey="minBottomBrandVideos" maxKey="maxBottomBrandVideos" />
                            <div className={`rounded-lg bg-green-50 p-1.5 -m-1.5 transition ${filters.cc !== 'all' ? 'ring-2 ring-green-500' : 'ring-1 ring-green-200'}`}>
                                <label className="text-[10px] uppercase font-bold text-green-700 block mb-1">✓ Creator Connection</label>
                                <div className="flex bg-green-100 rounded-lg p-0.5">
                                    {[{ value: 'all', label: 'All' }, { value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }].map(o => (
                                        <button key={o.value}
                                            onClick={() => setFilters(f => ({ ...f, cc: o.value }))}
                                            className={`flex-1 px-2 py-1.5 rounded text-[10px] font-bold uppercase tracking-wide transition ${filters.cc === o.value ? 'bg-white shadow text-green-700' : 'text-green-600/60 hover:text-green-700'}`}>
                                            {o.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <label className="text-[10px] uppercase font-semibold text-gray-400 block mb-1">Has Main Videos</label>
                                <div className="flex bg-gray-100 rounded-lg p-0.5">
                                    {[{ value: 'all', label: 'All' }, { value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }].map(o => (
                                        <button key={o.value}
                                            onClick={() => setFilters(f => ({ ...f, hasMainVideos: o.value }))}
                                            className={`flex-1 px-2 py-1.5 rounded text-[10px] font-bold uppercase tracking-wide transition ${filters.hasMainVideos === o.value ? 'bg-white shadow text-teal-700' : 'text-gray-400'}`}>
                                            {o.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Action Bar */}
                    <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                        <div className="flex gap-2">
                            <button onClick={resetFilters} className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition">
                                <RotateCcw size={12} /> Reset
                            </button>
                            {onExportCSV && (
                                <button onClick={onExportCSV} className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-teal-700 bg-teal-50 rounded-lg hover:bg-teal-100 transition">
                                    <Download size={12} /> Export CSV
                                </button>
                            )}
                        </div>
                        <div className="flex gap-2 relative">
                            <button onClick={() => { setShowSaveSlots(!showSaveSlots); setShowLoadSlots(false); }}
                                className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100 transition">
                                <Save size={12} /> Save
                            </button>
                            <button onClick={() => { setShowLoadSlots(!showLoadSlots); setShowSaveSlots(false); }}
                                className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-purple-700 bg-purple-50 rounded-lg hover:bg-purple-100 transition">
                                <FolderOpen size={12} /> Load
                            </button>
                            {showSaveSlots && (
                                <div className="absolute bottom-full right-0 mb-2 bg-white border border-gray-200 rounded-lg shadow-xl z-50 w-48 py-1">
                                    {[0, 1, 2, 3, 4, 5].map(i => (
                                        <button key={i} onClick={() => saveSlot(i)}
                                            className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50 transition flex items-center justify-between">
                                            <span>{slots[i]?.name || `Slot ${i + 1} (empty)`}</span>
                                            {slots[i] && <span className="text-green-500 text-[10px]">saved</span>}
                                        </button>
                                    ))}
                                </div>
                            )}
                            {showLoadSlots && (
                                <div className="absolute bottom-full right-0 mb-2 bg-white border border-gray-200 rounded-lg shadow-xl z-50 w-48 py-1">
                                    {[0, 1, 2, 3, 4, 5].map(i => (
                                        <button key={i} onClick={() => loadSlot(i)} disabled={!slots[i]}
                                            className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50 transition disabled:opacity-30 disabled:cursor-not-allowed">
                                            {slots[i]?.name || `Slot ${i + 1} (empty)`}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export { initialFilters };
export default AIPAdvancedFilters;
